import React from 'react'
import { useSelector } from 'react-redux'
import get from 'lodash/get'
import { Box } from 'pcln-design-system'
import MediaQueryMatch from '@pcln/media-query-match'
import ClientOnly from '../../../common-components/ClientOnly'
import { CrossSellGenericAd } from '../Legacy/cross-sell-generic-ad'
import { canShowHotelXSell } from '../../../../client/utils'
import { clickCrossSell } from '../../../common/log-ga-event/logGaEvent-cross-sell'
import { buildHotelListingUrlWithoutSelectedFlight } from '../../helpers/CrossSellHelpers/buildHotelCrossSellUrls'
import { handlePackageCrossSell } from '../../util/crossSellGenericAd'
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { parseISO } from 'date-fns/parseISO'
import { startOfDay } from 'date-fns/startOfDay'
import { calculateLengthOfStay } from '../../../details-client/util/length-of-stay'
import {
  getTripEndCityItems,
  getPurchaseWindowItems,
} from '../../../common/log-ga-event/logGaEvent-ecommerce'
import { getPassengersCountHelper } from '../../components/ListingsContent/helpers'
import { countFlightPassengers } from '../../../details-client/DetailsCTA/helper'

const buildEcommerceData = (slices = [], params = {}, pclnBootstrapData) => {
  const travelDate = slices[0]?.travelDateAsYYYYMMDD
  const returnDate = slices[1]?.travelDateAsYYYYMMDD
  const ap = differenceInCalendarDays(parseISO(travelDate), startOfDay(new Date()))
  const los = calculateLengthOfStay(travelDate, returnDate)
  const passengerCount = getPassengersCountHelper(params)
  const totalPassengerCount = countFlightPassengers({ passengers: passengerCount, pclnBootstrapData })
  const originAirport = slices[0]?.originAirport
  const destinationAirport = slices[0]?.destinationAirport
  const currency = pclnBootstrapData?.itinDetails?.pointOfSale?.currency?.toLowerCase() ?? 'usd'

  return {
    currency,
    items: [
      {
        quantity: totalPassengerCount,
        ...(originAirport && destinationAirport
          ? getTripEndCityItems([originAirport, destinationAirport])
          : {}),
        ...getPurchaseWindowItems(ap, los, 'RT'), // always RT — isCrossSellDisplayed requires isRoundTrip
      },
    ],
  }
}

const handleHotelCrossSellClick = (
  position,
  crossSellControlObject,
  isPartner,
  pclnBootstrapData,
  creativeSlot
) => {
  const { slices, params, numberOfPassengers } = crossSellControlObject

  const queryStringParams = {
    origin: slices[0].originAirport,
    'origin-type': slices[0].originAirportType || 'AIRPORT',
    destination: slices[1].originAirport,
    'origin-city': slices[0].originCityName,
    'destination-city': slices[0].destinationCityName,
    'destination-type': slices[0].destinationAirportType || 'AIRPORT',
    'departure-date': slices[0].travelDateAsYYYYMMDD,
    'return-date': slices[1].travelDateAsYYYYMMDD,
    'num-adults': params['num-adults'] || 0,
    'num-children': params['num-children'] || 0,
    'num-lap-infants': params['num-infants'] || 0,
    'num-rooms': numberOfPassengers > 4 ? 2 : 1,
    ...(isPartner && { wl_xsell: 'flightpathplacement' }),
  }

  clickCrossSell({
    pageName: 'listings',
    promotionId: 'hotel_static_xsell_banner',
    promotionName: 'hotel',
    creativeName: 'updated_hotel_static_xsell_banner',
    creativeSlot: creativeSlot || `listings_${position === 'top' ? 'top' : 'middle'}`,
    locationId: 'hero_banner',
    filterType: 'listings',
    bundleFlag: 'y',
    bundleType: 'AH',
    ecommerce: buildEcommerceData(slices, params, pclnBootstrapData),
  })

  const hotelCrossSellUrl = buildHotelListingUrlWithoutSelectedFlight({ queryString: queryStringParams })
  window.open(hotelCrossSellUrl)
}

const staticCrossSellBanner = (
  placement = 'top',
  crossSellControlObject,
  isPartner,
  pclnBootstrapData,
  creativeSlot
) => {
  const { slices, params } = crossSellControlObject
  const ecommerceData = buildEcommerceData(slices, params, pclnBootstrapData)

  return (
    <CrossSellGenericAd
      searchParams={crossSellControlObject}
      handleHotelCrossSellClick={() =>
        handleHotelCrossSellClick(
          placement,
          crossSellControlObject,
          isPartner,
          pclnBootstrapData,
          creativeSlot
        )
      }
      ecommerceData={ecommerceData}
      creativeSlot={creativeSlot}
    />
  )
}

export const canRenderXSellAd = (pclnBootstrapData, crossSellControlObject) => {
  const searchRequest = get(pclnBootstrapData, 'searchRequest')
  const showHotelXSell = canShowHotelXSell(searchRequest)
  const params = searchRequest?.params
  const numInfants = Number(params?.['num-infants'] || 0)
  const hasUSDestinationsOnly = pclnBootstrapData?.hasUSDestinationsOnly
  const conditionHideXsellInternationalLapInfant = hasUSDestinationsOnly || !numInfants

  return (
    get(crossSellControlObject, 'isCrossSell') && showHotelXSell && conditionHideXsellInternationalLapInfant
  )
}

/**
 * @typedef {Object} RecommendedPackageDealProps
 */

/**
 * @param {RecommendedPackageDealProps} props
 * @property {boolean} [isFlexDatesDisplayed]
 */
const RecommendedPackageDeal = ({ isFlexDatesDisplayed }) => {
  const { pclnBootstrapData } = useSelector((state) => ({
    pclnBootstrapData: get(state, 'pclnBootstrapData', {}),
  }))
  const isMobile = useSelector((state) => state?.pclnBootstrapData?.isMobile)

  const crossSellControlObject = handlePackageCrossSell(pclnBootstrapData)
  let xSellAdTop = null
  const params = pclnBootstrapData?.searchRequest?.params
  const isPackageListings = params?.['usp-cache-key'] && params?.['cart-key']

  if (
    !xSellAdTop &&
    canRenderXSellAd(pclnBootstrapData, crossSellControlObject) &&
    pclnBootstrapData?.features?.whiteLabel?.packagesCrossSellEnabled &&
    !isPackageListings
  ) {
    const isPartner = !!pclnBootstrapData?.partner

    xSellAdTop = (
      <Box mb={3} mt={isFlexDatesDisplayed ? 3 : 0} data-testid='PKG_CROSS_SELL_AD'>
        {staticCrossSellBanner('top', crossSellControlObject, isPartner, pclnBootstrapData)}
      </Box>
    )
  }
  const numInfants = Number(params?.['num-infants'] || 0)
  const hasUSDestinationsOnly = pclnBootstrapData?.hasUSDestinationsOnly
  const conditionHideXsellInternationalLapInfant = hasUSDestinationsOnly || !numInfants

  const shouldRenderXSellAd =
    xSellAdTop && !pclnBootstrapData?.isReshopExperience && conditionHideXsellInternationalLapInfant
  if (!shouldRenderXSellAd) return null

  if (isMobile) {
    // Applying NoSSR on mobile because MediaQueryMatch
    // causes quick render flicker because window isn't defined.
    // TODO: Refactor using Hide Design System component,
    // or remove when if MediaQueryMatch is removed
    return (
      <ClientOnly>
        <MediaQueryMatch minWidth={'lg'}>{xSellAdTop}</MediaQueryMatch>
      </ClientOnly>
    )
  }

  return <MediaQueryMatch minWidth={'lg'}>{xSellAdTop}</MediaQueryMatch>
}

export default React.memo(RecommendedPackageDeal)

export const XSellAd = (props) => {
  const { pclnBootstrapData, crossSellControlObject, isPackageListings, bannerIndex } = props
  const isPartner = !!pclnBootstrapData?.partner

  if (
    canRenderXSellAd(pclnBootstrapData, crossSellControlObject) &&
    pclnBootstrapData?.features?.whiteLabel?.packagesCrossSellEnabled &&
    !isPackageListings
  ) {
    return staticCrossSellBanner('middle', crossSellControlObject, isPartner, pclnBootstrapData, bannerIndex)
  }
}
